import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { BrowserRouter } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { MsalProvider } from '@azure/msal-react';
import { EventType, type AccountInfo, type EventMessage } from '@azure/msal-browser';
import { App } from './App';
import { msalInstance, ensureMsalInitialized } from './auth/index';
import { RoleProvider } from './auth/RoleContext';

/**
 * Entry point. Mirrors the Reports kit's main.tsx provider stack:
 *   MsalProvider -> QueryClientProvider -> BrowserRouter -> RoleProvider -> App
 * with MSAL initialized BEFORE first render (msal-browser v3 requirement) and the
 * active account pinned/synced so token acquisition targets the signed-in user.
 *
 * RoleProvider sits inside MsalProvider+Router so it can read the MSAL account's
 * App Roles and so <RequireRole> (which uses react-router's <Navigate>) works.
 */
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      staleTime: 30_000,
      gcTime: 60_000,
      retry: (failureCount, error) => {
        const status = (error as { status?: number })?.status;
        if (status && status >= 400 && status < 500) return false;
        return failureCount < 2;
      },
    },
  },
});

const rootEl = document.getElementById('root');
if (!rootEl) throw new Error('Root element #root not found in index.html');

ensureMsalInitialized()
  .catch((e) => {
    // eslint-disable-next-line no-console
    console.warn('MSAL init failed (dev-mode auth bypass will engage)', e);
  })
  .finally(() => {
    // Pin the active account so token acquisition mints for the SIGNED-IN identity
    // rather than getAllAccounts()[0]. Same pattern as the Reports kit.
    try {
      if (!msalInstance.getActiveAccount()) {
        const first = msalInstance.getAllAccounts()[0];
        if (first) msalInstance.setActiveAccount(first);
      }
      msalInstance.addEventCallback((event: EventMessage) => {
        if (
          (event.eventType === EventType.LOGIN_SUCCESS ||
            event.eventType === EventType.ACQUIRE_TOKEN_SUCCESS) &&
          event.payload && 'account' in event.payload && event.payload.account
        ) {
          msalInstance.setActiveAccount(event.payload.account as AccountInfo);
        }
      });
    } catch (e) {
      // eslint-disable-next-line no-console
      console.warn('MSAL active-account wiring failed', e);
    }

    createRoot(rootEl).render(
      <StrictMode>
        <MsalProvider instance={msalInstance}>
          <QueryClientProvider client={queryClient}>
            <BrowserRouter>
              <RoleProvider>
                <App />
              </RoleProvider>
            </BrowserRouter>
          </QueryClientProvider>
        </MsalProvider>
      </StrictMode>,
    );
  });
